import type { ReactNode } from "react";
import { ChevronDownIcon } from "@chakra-ui/icons";
import { Flex, type ButtonProps } from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import { portalsModel } from "models/portals";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { SelectInput } from "components/inputs/select-input";
import { useAuth } from "contexts/auth-provider";
import { usePortalPath } from "hooks/use-portal-path";

type PortalSelectorProps = {
  menuButtonProps?: ButtonProps;
  overLineTextColor?: string;
  overlineText?: string;
  prefixIcon?: ReactNode;
  shouldUseClientBranding?: boolean;
};

export function PortalSelector({
  menuButtonProps,
  overlineText,
  prefixIcon,
  overLineTextColor,
}: PortalSelectorProps) {
  const { propertyId, regionId } = useParams();
  const location = useLocation();
  const { isTenantPath } = usePortalPath();

  const { data: portals = [] } = useQuery({
    ...portalsModel.queries.list(propertyId),
    enabled: !isTenantPath,
  });

  const auth = useAuth();
  const navigate = useNavigate();

  if (!portals) return;

  const groups = [
    {
      label: { id: "listing", value: "For Sale" },
      options: portals.map((l) => ({
        label: l.label || l.id,
        value: String(l.remote_record_id),
      })),
    },
  ];

  const isLandlordPath = location.pathname.startsWith("/landlord");

  const selectedGroup =
    auth.isLandlord && isLandlordPath
      ? groups[0]
      : groups.find(
          (option) =>
            option?.options?.find(
              (option) => String(option?.value) === String(propertyId),
            ),
        );

  const selectedGroupOption =
    selectedGroup?.label.id === "landlord"
      ? selectedGroup.options[0]
      : selectedGroup?.options.find(
          (option) => String(option?.value) === String(propertyId),
        );

  return (
    <Flex h={12} align={"center"} justify={"center"} gap={2}>
      <SelectInput
        optionGroups={groups}
        value={selectedGroupOption?.value}
        onChange={(value, label) => {
          const group = groups.find((group) => group.label.id === label);
          const option = group?.options.find(
            (option) => String(option.value) === value,
          );
          navigate(
            option
              ? `/${regionId}/${label}/${option.value}`
              : `/${regionId}/${label}`,
          );
        }}
        menuProps={{
          matchWidth: true,
          gutter: 0,
          autoSelect: false,
          isLazy: true,
        }}
        menuButtonProps={{
          variant: "outline",
          fontSize: "lg",
          py: 2,
          h: "auto",
          colorScheme: "gray",
          borderWidth: "2px",
          borderColor: "rgba(0,0,0,0)",
          _active: {
            bg: "indigo.100",
            borderColor: "indigo.600",
          },
          _hover: {
            bg: "indigo.100",
          },
          ...menuButtonProps,
        }}
        overlineTextColor={overLineTextColor}
        overlineText={overlineText}
        prefixIcon={prefixIcon}
        rightIcon={<ChevronDownIcon boxSize={"18px"} />}
      />
    </Flex>
  );
}
